// Métricas de uma campanha a partir dos destinatários (fórmulas da seção 3.5 do SPEC.md).
// A versão do servidor (congelamento aos 30 dias) segue as mesmas fórmulas.
import type { CampaignStatus, PushCampaign, PushRecipient } from '../types/push';

/** Campanha em "sending" há mais que isso: a função provavelmente caiu no meio. */
const INTERRUPTED_AFTER_MS = 10 * 60 * 1000;

const ERROR_LABELS: Record<string, string> = {
  'messaging/registration-token-not-registered': 'App desinstalado ou token expirado',
  'messaging/invalid-registration-token': 'Token inválido',
  'messaging/third-party-auth-error': 'Chave APNs recusada (configuração do projeto)',
  'messaging/message-rate-exceeded': 'Limite de envios para o aparelho',
  'messaging/device-message-rate-exceeded': 'Limite de envios para o aparelho',
  'messaging/server-unavailable': 'Instabilidade no FCM',
  'messaging/internal-error': 'Instabilidade no FCM',
  'messaging/invalid-argument': 'Conteúdo rejeitado pelo FCM',
};

export const pushErrorLabel = (code: string | null) => (code && ERROR_LABELS[code]) || code || 'Erro desconhecido';

export interface CampaignMetrics {
  targeted: number;
  sent: number;
  invalidToken: number;
  skippedInvalid: number;
  failed: number;
  trackable: number;        // enviadas para app que reporta (appVersionAtSend preenchido)
  trackableAndroid: number;
  receivedAndroid: number;
  opened: number;
  failuresByCode: { code: string; label: string; count: number }[];
}

export const computeCampaignMetrics = (recipients: PushRecipient[]): CampaignMetrics => {
  const metrics: CampaignMetrics = {
    targeted: recipients.length,
    sent: 0,
    invalidToken: 0,
    skippedInvalid: 0,
    failed: 0,
    trackable: 0,
    trackableAndroid: 0,
    receivedAndroid: 0,
    opened: 0,
    failuresByCode: [],
  };
  const failures: Record<string, number> = {};

  for (const recipient of recipients) {
    if (recipient.status === 'sent') metrics.sent += 1;
    if (recipient.status === 'invalid_token') metrics.invalidToken += 1;
    if (recipient.status === 'skipped_invalid') metrics.skippedInvalid += 1;
    if (recipient.status === 'failed') {
      metrics.failed += 1;
      const code = recipient.errorCode || 'unknown';
      failures[code] = (failures[code] || 0) + 1;
    }

    const trackable = recipient.status === 'sent' && !!recipient.appVersionAtSend;
    if (trackable) metrics.trackable += 1;
    if (trackable && recipient.platform === 'android') {
      metrics.trackableAndroid += 1;
      if (recipient.receivedAt || recipient.openedAt) metrics.receivedAndroid += 1;
    }
    if (recipient.openedAt) metrics.opened += 1;
  }

  metrics.failuresByCode = Object.keys(failures)
    .map(code => ({ code, label: pushErrorLabel(code), count: failures[code] }))
    .sort((a, b) => b.count - a.count);
  return metrics;
};

export type Tone = 'success' | 'info' | 'warning' | 'error' | 'muted';

/** Situação de um aparelho na campanha, como aparece na tabela de destinatários. */
export const recipientSituation = (recipient: PushRecipient): { label: string; tone: Tone } => {
  if (recipient.openedAt) return { label: 'Abriu', tone: 'success' };
  if (recipient.receivedAt) return { label: 'Recebeu', tone: 'success' };
  switch (recipient.status) {
    case 'sent':
      return recipient.permissionAtSend === 'denied'
        ? { label: 'Enviada · notificações desativadas no aparelho', tone: 'warning' }
        : { label: 'Enviada', tone: 'info' };
    case 'invalid_token':
      return { label: 'App desinstalado ou token expirado', tone: 'muted' };
    case 'skipped_invalid':
      return { label: 'Inativo — não enviado', tone: 'muted' };
    default:
      return { label: `Falhou: ${pushErrorLabel(recipient.errorCode)}`, tone: 'error' };
  }
};

const STATUS_LABELS: Record<CampaignStatus, { label: string; tone: Tone }> = {
  scheduled: { label: 'Agendada', tone: 'info' },
  sending: { label: 'Enviando', tone: 'info' },
  sent: { label: 'Concluída', tone: 'success' },
  failed: { label: 'Falhou', tone: 'error' },
  canceled: { label: 'Cancelada', tone: 'muted' },
};

/** Status exibido: "sending" parado há mais de 10 minutos vira "Interrompida". */
export const campaignStatusLabel = (campaign: PushCampaign, now = Date.now()): { label: string; tone: Tone } => {
  if (
    campaign.status === 'sending' &&
    campaign.sendingStartedAt &&
    now - campaign.sendingStartedAt.getTime() > INTERRUPTED_AFTER_MS
  ) {
    return { label: 'Interrompida', tone: 'warning' };
  }
  return STATUS_LABELS[campaign.status] || { label: campaign.status, tone: 'muted' };
};

export const TONE_COLORS: Record<Tone, { background: string; color: string }> = {
  success: { background: '#e8f5e9', color: '#2e7d32' },
  info: { background: '#e3f2fd', color: '#1565c0' },
  warning: { background: '#fff8e1', color: '#8a5300' },
  error: { background: '#ffebee', color: '#c62828' },
  muted: { background: '#f0f0f0', color: '#757575' },
};

export const formatDateTime = (date: Date | null) =>
  date ? date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const percent = (part: number, total: number) => (total > 0 ? `${Math.round((part / total) * 100)}%` : '—');
